import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type {
  NavigationItem,
  NavigationPageItem,
} from '@locus-taxy/encore-mui';
import { useModules } from '../api/endpoints/useModules';
import type { ModuleItem } from '../api/endpoints/useModules';
import {
  buildModulesNavigation,
  findFirstLeafPath,
  findMatchingLeaf,
  stripClientPrefix,
  type ModuleLeafRoute,
} from '../utils/modulesNavigation';

const EMPTY_NAVIGATION_SIGNATURE = '[]';

function buildNavigationSignature(items: NavigationItem[]): string {
  const serialize = (
    entries: NavigationItem[]
  ): Array<Record<string, unknown>> =>
    entries.map(entry => {
      if (entry.kind === 'header') {
        return { kind: 'header', title: entry.title };
      }
      if (entry.kind === 'divider') {
        return { kind: 'divider' };
      }

      const pageEntry = entry as NavigationPageItem;
      return {
        segment: pageEntry.segment,
        title: pageEntry.title,
        children: serialize(pageEntry.children ?? []),
      };
    });

  try {
    return JSON.stringify(serialize(items));
  } catch {
    return EMPTY_NAVIGATION_SIGNATURE;
  }
}

interface UseAppNavigationResult {
  navigation: NavigationItem[];
  moduleLeaves: ModuleLeafRoute[];
  navigationSignature: string;
  firstLeafPath: string | null;
  activeNavPath: string | undefined;
  isModulesLoading: boolean;
}

export function useAppNavigation(clientId: string): UseAppNavigationResult {
  const location = useLocation();
  const { data: modulesResponse, isLoading: isModulesLoading } =
    useModules(clientId);

  const { navigation, leaves: moduleLeaves } = useMemo(() => {
    if (!clientId) {
      return {
        navigation: [] as NavigationItem[],
        leaves: [],
      };
    }

    const rootItems =
      modulesResponse?.data && 'modules' in modulesResponse.data
        ? (modulesResponse.data as { modules?: ModuleItem[] }).modules
        : [];

    return buildModulesNavigation(rootItems, clientId);
  }, [modulesResponse?.data, clientId]);

  const navigationSignature = useMemo(
    () => buildNavigationSignature(navigation),
    [navigation]
  );

  const firstLeafPath = useMemo(
    () => findFirstLeafPath(moduleLeaves),
    [moduleLeaves]
  );

  // Compute the cumulative segment path that DashboardLayout expects for activePath.
  // The navigation uses backtracking segments (../), so location.pathname won't match
  // the cumulative paths that DashboardLayout builds internally. We find the matching
  // leaf and return its pre-computed navSegmentPath.
  const activeNavPath = useMemo(() => {
    if (!moduleLeaves.length) {
      return undefined;
    }
    const clientless = stripClientPrefix(location.pathname);
    const match = findMatchingLeaf(clientless, moduleLeaves);
    return match?.navSegmentPath;
  }, [location.pathname, moduleLeaves]);

  return {
    navigation,
    moduleLeaves,
    navigationSignature,
    firstLeafPath,
    activeNavPath,
    isModulesLoading,
  };
}
